import { Tooltip } from "antd";
import dayjs from "dayjs";
import { ReactNode, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { renderChartEmptyState } from "../../../../../../components/ChartEmptyState";
import { CustomModalChartTick } from "../../../../../../components/ModalChart/CustomModalChartTick/CustomModalChartTick";
import { getFriendlySourceName } from "../../../../../../utils/chartSourceUtils";
import {
  getEmptyAllAxis,
  getGraphSeparators,
} from "../../../../../../utils/chartUtils";
import { DATE_FILTER_ALL, DateFilterOptionType } from "../CustomOptionsBar";
import { TooltipBox, TooltipDate, TooltipValue } from "./StepsBarChart.styles";

export type StepsChartRow = {
  timeValue: number;
  [source: string]: number | undefined;
};

export type StepsChartData = {
  rows: StepsChartRow[];
  sources: string[];
};

// Per-source bar colors so each source is visually distinguishable. Sources
// not in this map are assigned a color from FALLBACK_PALETTE by position in
// the chart's `sources` array (rotates after the palette is exhausted).
// Palette colors are reused from existing `sourceConstants` so we stay
// consistent with how other charts color those sources.
const STEPS_SOURCE_COLORS: Record<string, string> = {
  fitbit: "#C87C36",
  "google-health": "#54A6AA",
  "google-fit": "#5CAB57",
};
const FALLBACK_PALETTE = [
  "#C87C36", // orange (MIR / Fitbit active)
  "#2196f3", // blue (Aiocare / Fibricheck)
  "#CE0E2D", // red (Masimo MightySat Rx)
  "#457B9D", // slate blue (Dialysis)
];

export const STEPS_BAR_COLOR = STEPS_SOURCE_COLORS.fitbit;

export function getStepsSourceColor(source: string, index = 0): string {
  return (
    STEPS_SOURCE_COLORS[source.toLowerCase()] ??
    FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]
  );
}

/**
 * Flattens the parsed measurements map (keyed by `daily_steps|source`) into
 * wide rows: one row per day with a value per source key (e.g.
 * `{ timeValue, fitbit: 4521, "google-health": 6240 }`). Each reading is
 * centered at noon of its day so bars align to the daily axis grid.
 *
 * Returns the rows plus the list of unique sources present in the dataset,
 * so the chart and legend can render one bar/dot per source.
 */
export function buildStepsChartData(data: Map<string, any[]>): StepsChartData {
  const sources = new Set<string>();
  const byDay = new Map<number, StepsChartRow>();

  Array.from(data.values())
    .flat()
    .forEach((point) => {
      const source = (point?.source ?? "").toLowerCase();
      if (!source) return;
      sources.add(source);

      const day = dayjs(point.unformattedTime)
        .startOf("day")
        .add(12, "hour")
        .valueOf();

      const existing = byDay.get(day) ?? { timeValue: day };
      existing[source] = point.daily_steps ?? 0;
      byDay.set(day, existing);
    });

  return {
    rows: Array.from(byDay.values()).sort((a, b) => a.timeValue - b.timeValue),
    sources: Array.from(sources),
  };
}

/**
 * Aggregates daily rows into weekly rows: one row per ISO week (Monday at
 * noon) with the *mean* steps per source for that week. Used when the
 * selected range is too wide for daily bars to be readable (> 6 months).
 */
export function aggregateStepsByWeek(input: StepsChartData): StepsChartData {
  const { rows, sources } = input;
  type WeekEntry = {
    timeValue: number;
    sums: Record<string, number>;
    counts: Record<string, number>;
  };
  const byWeek = new Map<number, WeekEntry>();

  rows.forEach((row) => {
    const d = dayjs(row.timeValue);
    const dow = d.day(); // 0=Sun, 1=Mon ... 6=Sat
    const offsetToMonday = dow === 0 ? -6 : 1 - dow;
    const weekStart = d
      .add(offsetToMonday, "day")
      .startOf("day")
      .add(12, "hour")
      .valueOf();

    let entry = byWeek.get(weekStart);
    if (!entry) {
      entry = { timeValue: weekStart, sums: {}, counts: {} };
      byWeek.set(weekStart, entry);
    }

    sources.forEach((source) => {
      const v = row[source];
      if (typeof v === "number") {
        entry!.sums[source] = (entry!.sums[source] ?? 0) + v;
        entry!.counts[source] = (entry!.counts[source] ?? 0) + 1;
      }
    });
  });

  const weeklyRows: StepsChartRow[] = Array.from(byWeek.values())
    .map((entry) => {
      const row: StepsChartRow = { timeValue: entry.timeValue };
      sources.forEach((source) => {
        const count = entry.counts[source];
        if (count > 0) {
          row[source] = Math.round((entry.sums[source] ?? 0) / count);
        }
      });
      return row;
    })
    .sort((a, b) => a.timeValue - b.timeValue);

  return { rows: weeklyRows, sources };
}

type StepsBarChartProps = {
  data: StepsChartRow[];
  sources: string[];
  width: string | number;
  height: string | number;
  dateInterval: DateFilterOptionType;
  isPinnedGraph?: boolean;
  onlyGraph?: boolean;
  isEmpty?: boolean;
  legend?: ReactNode;
  weeklyAggregation?: boolean;
};

type HoveredBar = { idx: number; source: string };

export function StepsBarChart({
  data,
  sources,
  width,
  height,
  dateInterval,
  isPinnedGraph,
  onlyGraph,
  isEmpty,
  legend,
  weeklyAggregation,
}: StepsBarChartProps) {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState<ReactNode>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const [hoveredBar, setHoveredBar] = useState<HoveredBar | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleBarMouseMove =
    (source: string) => (barData: any, idx: number, e: any) => {
      const { clientX, clientY } = e;
      setPos((prev) =>
        prev.left === clientX && prev.top === clientY
          ? prev
          : { left: clientX, top: clientY }
      );

      setHoveredBar({ idx, source });

      const row = (barData?.payload ?? barData) as StepsChartRow | undefined;
      const value = row?.[source];
      if (row && typeof value === "number") {
        const monday = dayjs(row.timeValue);
        const sunday = monday.add(6, "day");
        const dateLabel = weeklyAggregation
          ? t("patientPage_measurementModal_steps_weekOf", {
              start: monday.format("MMM D"),
              end: sunday.format("MMM D, YYYY"),
            })
          : monday.format("MMM D, YYYY");
        const valueLabel = t(
          weeklyAggregation
            ? "patientPage_measurementModal_steps_averageValue"
            : "patientPage_measurementModal_steps_value",
          { value: value.toLocaleString() }
        );

        setTooltipTitle(
          <TooltipBox>
            <TooltipValue>{valueLabel}</TooltipValue>
            <TooltipDate>
              {`${getFriendlySourceName(source, t)} · ${dateLabel}`}
            </TooltipDate>
          </TooltipBox>
        );
      }
      setTooltipOpen(true);
    };

  const handleBarMouseLeave = () => {
    setTooltipOpen(false);
    setHoveredBar(null);
  };

  const minDate =
    data.length > 0
      ? dayjs(Math.min(...data.map((d) => d.timeValue)))
      : undefined;
  const maxDate =
    data.length > 0
      ? dayjs(Math.max(...data.map((d) => d.timeValue)))
      : undefined;

  const dataPointTicks = useMemo(() => data.map((d) => d.timeValue), [data]);

  const emptyAllAxis = getEmptyAllAxis(dateInterval, isEmpty);

  const {
    graphSeparators,
    dates,
    separatorCounter,
    viewType,
    isTickMultipleLine,
    interval,
    tickFormat: baseTickFormat,
  } = getGraphSeparators(
    dateInterval,
    emptyAllAxis?.anchor ?? (isEmpty ? dateInterval.startDate : minDate),
    emptyAllAxis?.anchor ?? (isEmpty ? dateInterval.endDate : maxDate)
  );

  // Short ranges align ticks and reference lines to the actual data points
  const isShortIntervalMode = interval === "48H" || interval === "24H";
  const tickFormat = isShortIntervalMode ? "MMM D" : baseTickFormat;

  // For the quarterly axis (~1-4 year ranges), getGraphSeparators emits the
  // start-of-current-month as a leading tick alongside the true quarter
  // starts (Jan/Apr/Jul/Oct). When the data starts mid-quarter that produces
  // two ticks in the same quarter (e.g. "Q1, 25" twice). Keep only true
  // quarter starts so each quarter is labelled once.
  const cleanGraphSeparators = useMemo(() => {
    if (viewType === "yearly" && tickFormat === "Q") {
      return graphSeparators.filter((ts) => {
        const d = dayjs(ts);
        return d.date() === 1 && [0, 3, 6, 9].includes(d.month());
      });
    }
    return graphSeparators;
  }, [graphSeparators, viewType, tickFormat]);

  const xAxisTicks = useMemo(() => {
    let ticks: number[];
    if (isShortIntervalMode) ticks = dataPointTicks;
    else if (viewType === "yearly") ticks = cleanGraphSeparators;
    else ticks = dates;
    return Array.from(new Set(ticks));
  }, [
    isShortIntervalMode,
    dataPointTicks,
    viewType,
    cleanGraphSeparators,
    dates,
  ]);

  const refs = useMemo((): number[] => {
    const combined = isShortIntervalMode
      ? dataPointTicks
      : [
          ...cleanGraphSeparators,
          ...dates.filter((_, index) => index % separatorCounter === 0),
        ];
    return Array.from(new Set(combined));
  }, [
    isShortIntervalMode,
    dataPointTicks,
    cleanGraphSeparators,
    dates,
    separatorCounter,
  ]);

  const tickXFormatter = (value: string) => {
    if (!isEmpty && data.length === 0) return "";

    const numValue = Number(value);

    if (isShortIntervalMode) {
      return dataPointTicks.includes(numValue)
        ? dayjs(numValue).format(tickFormat)
        : "";
    }

    if (cleanGraphSeparators.includes(numValue) || refs.includes(numValue)) {
      if (tickFormat === "Q") {
        return `Q${dayjs(value).format(tickFormat)}, ${dayjs(value).format(
          "YY"
        )}`;
      }
      return dayjs(value).format(tickFormat);
    }

    return "";
  };

  const handleXDomain = (): [number, number] => {
    if (!dateInterval) return [0, 1];
    if (!isEmpty && data.length === 0) return [0, 1];

    if (emptyAllAxis) return emptyAllAxis.domain;

    if (dateInterval.id === DATE_FILTER_ALL && minDate && maxDate) {
      const padding = 12 * 60 * 60 * 1000; // 12 hours
      return [
        minDate.startOf("day").valueOf() - padding,
        maxDate.endOf("day").valueOf() + padding,
      ];
    }

    const { startDate } = dateInterval;
    let { endDate } = dateInterval;

    if (isShortIntervalMode) {
      const padding = 12 * 60 * 60 * 1000;
      return [
        startDate.startOf("day").valueOf() - padding,
        endDate.endOf("day").valueOf() + padding,
      ];
    }

    if (endDate.diff(startDate, "months") > 11) {
      endDate = endDate.add(30, "day");
    }

    const padding = 12 * 60 * 60 * 1000;
    return [startDate.valueOf() - padding, endDate.valueOf() + padding];
  };

  // Round Y domain up to a clean tick across all sources' values.
  const maxYDomain = useMemo(() => {
    if (data.length === 0) return 100;
    const allValues = data.flatMap((row) =>
      sources.map((source) => row[source] ?? 0)
    );
    const max = allValues.length > 0 ? Math.max(...allValues) : 0;
    if (max <= 0) return 100;
    const step = max > 2000 ? 1000 : 200;
    return Math.ceil((max * 1.05) / step) * step;
  }, [data, sources]);

  // Narrow each bar when there are several sources sharing a day so the
  // grouped bars don't overflow horizontally.
  const barSize = (() => {
    if (onlyGraph) return 6;
    if (sources.length <= 1) return 28;
    return Math.max(8, Math.floor(28 / sources.length));
  })();

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        paddingRight: "12px",
        minWidth: isEmpty ? 600 : undefined,
      }}
    >
      {!onlyGraph ? (
        <Tooltip
          open={tooltipOpen}
          title={tooltipTitle}
          placement="top"
          zIndex={3000}
          arrow={false}
          styles={{
            body: {
              padding: 0,
              backgroundColor: "transparent",
            },
          }}
          mouseEnterDelay={0.1}
        >
          <span
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              width: 1,
              height: 1,
            }}
          />
        </Tooltip>
      ) : null}

      <ResponsiveContainer
        debounce={10}
        width={width}
        height={height}
        minWidth={isPinnedGraph ? 658 : undefined}
      >
        <BarChart
          style={{ width: "100%", maxHeight: "70vh", cursor: "pointer" }}
          data={data}
          margin={
            onlyGraph
              ? { top: 5, right: 5, left: 5, bottom: 5 }
              : { top: 5, right: 32, left: 5, bottom: 5 }
          }
        >
          {!onlyGraph ? (
            <CartesianGrid strokeDasharray="2" vertical={false} />
          ) : null}

          <XAxis
            tickMargin={14}
            stroke="#D8D8D8"
            type="number"
            scale="time"
            ticks={xAxisTicks}
            domain={handleXDomain()}
            tickFormatter={tickXFormatter}
            allowDuplicatedCategory={false}
            tickLine={false}
            height={refs.length >= 6 ? 70 : 48}
            minTickGap={refs.length >= 6 ? 20 : 100}
            interval={0}
            hide={onlyGraph}
            dataKey="timeValue"
            tick={
              isTickMultipleLine && !isShortIntervalMode ? (
                <CustomModalChartTick />
              ) : (
                {
                  fill: isEmpty ? "#B5B5B6" : "#727272",
                  fontSize: "12px",
                  fontWeight: "400",
                }
              )
            }
            angle={refs.length >= 6 ? -45 : 0}
          />

          <YAxis
            tickMargin={8}
            stroke="#D8D8D8"
            domain={[0, maxYDomain]}
            width={48}
            hide={onlyGraph}
            tick={{
              fill: isEmpty ? "#B5B5B6" : "#727272",
              fontSize: "12px",
              fontWeight: 400,
            }}
            allowDecimals={false}
          />

          {!onlyGraph
            ? refs.map((ref) => (
                <ReferenceLine
                  key={ref.toString()}
                  x={ref}
                  stroke="#D8D8D8"
                  strokeDasharray="8 1000"
                />
              ))
            : null}

          {!onlyGraph && legend ? (
            <Legend content={() => legend as any} />
          ) : null}

          {sources.map((source, sourceIdx) => (
            <Bar
              key={source}
              dataKey={source}
              fill={getStepsSourceColor(source, sourceIdx)}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
              barSize={barSize}
              onMouseMove={handleBarMouseMove(source)}
              onMouseLeave={handleBarMouseLeave}
            >
              {data.map((row, idx) => (
                <Cell
                  key={row.timeValue}
                  opacity={
                    hoveredBar?.idx === idx && hoveredBar.source === source
                      ? 0.8
                      : 1
                  }
                />
              ))}
            </Bar>
          ))}

          {isEmpty &&
            renderChartEmptyState({
              message: t("chart_noDataForRange"),
            })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
